'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { NextIntlClientProvider } from 'next-intl';

import SubriteError from 'components/SubriteError';

type ErrorProps = {
  error: Error;
  reset: () => void;
  digest?: string;
};

// Define the expected structure of the messages object
type SubriteErrorMessages = {
  title: string;
  text: string;
  tryAgain: string;
};

type Messages = {
  subriteError: SubriteErrorMessages;
};

const translations = {
  en: {
    subriteError: {
      title: 'An error has occurred',
      text: 'An error occurred with Subrite. Try again or <link>send us an email</link> to report the issue.',
      tryAgain: 'Try again',
    },
  },
  no: {
    subriteError: {
      title: 'Det har oppstått en feil',
      text: 'Det oppstod en feil med Subrite. Prøv igjen eller <link>send oss en e-post</link> for å rapportere problemet.',
      tryAgain: 'Prøv igjen',
    },
  },
} satisfies Record<'en' | 'no', Messages>; // ✅ Ensures translation object matches Messages

type SupportedLocale = keyof typeof translations;

function isSupportedLocale(locale: string): locale is SupportedLocale {
  return locale in translations;
}

function getUserLocale(): SupportedLocale {
  if (typeof navigator !== 'undefined' && navigator.language) {
    const baseLocale = navigator.language.split('-')[0];
    if (isSupportedLocale(baseLocale)) return baseLocale;
  }
  return 'no';
}

function getMessagesForLocale(locale: string): Messages {
  return isSupportedLocale(locale) ? translations[locale] : translations['en'];
}

function GlobalError({ error, reset }: ErrorProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const userLocale = getUserLocale();
  const messages = getMessagesForLocale(userLocale);

  return (
    <html lang={userLocale}>
      <body>
        <div className="flex flex-col items-center justify-center w-full min-h-full px-4 bg-[var(--themeBackgroundColor)] md:px-8">
          <NextIntlClientProvider locale={userLocale} messages={messages}>
            <SubriteError reset={reset} />
          </NextIntlClientProvider>
        </div>
      </body>
    </html>
  );
}

export default GlobalError;
